import { useState, useContext, createContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Copy, Printer, Sparkles, Stethoscope, Save, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import {
  conv, a1cToEag, glucoseToA1c, bmi, bpCategory, egfrCKDEPI, cockcroftGault,
  cholesterolRisk, ldlFriedewald, nonHdl, targetHeartRate, dueDate, weightBasedDose,
  idealBodyWeight, waistToHeight, pediatricFever, PED_CONC, doseToMl, toolBySlug, COLOR, TONE,
} from "../../lib/tools";
import { useToolsT } from "../../lib/toolsI18n";
import { saveToolEntry } from "../../lib/toolHistory";
import { EmailSummaryForm } from "../EmailSummary";

// Provides the active tool slug so ResultActions can offer on-device saving.
export const ToolSlugContext = createContext(null);

// --- Shared primitives ---
export const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
  </label>
);

const inputCls =
  "w-full bg-white border-2 border-slate-200 focus:border-teal-400 rounded-xl px-4 py-2.5 text-slate-900 text-lg outline-none transition-colors";

export const NumberInput = ({ value, onChange, placeholder, step = "any", testId }) => (
  <input type="number" inputMode="decimal" step={step} value={value}
    onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className={inputCls} />
);

const SexToggle = ({ value, onChange, tt }) => (
  <div className="flex rounded-xl border-2 border-slate-200 overflow-hidden" data-testid="sex-toggle">
    {[["female", tt("female")], ["male", tt("male")]].map(([s, label]) => (
      <button key={s} type="button" onClick={() => onChange(s)} data-testid={`sex-${s}`}
        className={`flex-1 py-2.5 text-base font-semibold transition-colors ${value === s ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-teal-50"}`}>
        {label}
      </button>
    ))}
  </div>
);

const UnitToggle = ({ options, value, onChange }) => (
  <div className="inline-flex rounded-lg border-2 border-slate-200 overflow-hidden" data-testid="unit-toggle">
    {options.map((o) => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`unit-${o.value}`}
        className={`px-3 py-1.5 text-sm font-semibold transition-colors ${value === o.value ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
        {o.label}
      </button>
    ))}
  </div>
);

function ResultActions({ summary, question, tt }) {
  const navigate = useNavigate();
  const slug = useContext(ToolSlugContext);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      toast.success(tt("toastCopied"));
    } catch {
      toast.error("Copy failed");
    }
  };
  const save = () => {
    const value = summary
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(1)
      .join(" · ");
    saveToolEntry(slug, value);
    toast.success(tt("toastSaved"));
  };
  const print = () => {
    const w = window.open("", "_blank", "width=600,height=700");
    if (!w) return;
    w.document.write(`<pre style="font-family:system-ui,Arial;font-size:15px;white-space:pre-wrap;padding:24px;line-height:1.6">${summary
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")}</pre>`);
    w.document.close();
    w.focus();
    w.print();
  };
  const btn = "inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border-2 transition-colors";
  return (
    <>
    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-black/10">
      <button onClick={copy} data-testid="result-copy" className={`${btn} border-slate-300 text-slate-700 hover:bg-white/60`}>
        <Copy className="w-4 h-4" /> {tt("copy")}
      </button>
      <button onClick={print} data-testid="result-print" className={`${btn} border-slate-300 text-slate-700 hover:bg-white/60`}>
        <Printer className="w-4 h-4" /> {tt("print")}
      </button>
      {slug && (
        <button onClick={save} data-testid="result-save" className={`${btn} border-slate-300 text-slate-700 hover:bg-white/60`}>
          <Save className="w-4 h-4" /> {tt("save")}
        </button>
      )}
      {question && (
        <button onClick={() => navigate(`/answer?q=${encodeURIComponent(question)}`)} data-testid="result-explain"
          className={`${btn} border-teal-500 bg-teal-600 text-white hover:bg-teal-700`}>
          <Sparkles className="w-4 h-4" /> {tt("explain")}
        </button>
      )}
      {question && (
        <button onClick={() => navigate(`/answer?q=${encodeURIComponent(question)}&review=1`)} data-testid="result-review"
          className={`${btn} border-violet-500 text-violet-700 hover:bg-violet-50`}>
          <Stethoscope className="w-4 h-4" /> {tt("sendReview")}
        </button>
      )}
      </div>
      <EmailSummaryForm title={tt("summaryTitle")} summary={summary} source={slug || "tool"} />
    </>
  );
}

const Result = ({ show, tone = "neutral", summary, question, tt, children }) =>
  show ? (
    <div className={`mt-6 rounded-2xl border-2 p-5 ${TONE[tone]}`} data-testid="tool-result">
      {children}
      {summary && <ResultActions summary={summary} question={question} tt={tt} />}
    </div>
  ) : null;

const num = (v) => (v === "" || v === null || v === undefined ? NaN : Number(v));
const ok = (...vals) => vals.every((v) => Number.isFinite(v) && v > 0);
const mmolBtn = (tt) => [{ value: "mgdl", label: "mg/dL" }, { value: "mmol", label: "mmol/L" }];

// ---------- 1. A1C -> eAG ----------
function A1cToGlucose() {
  const { tt } = useToolsT();
  const [a1c, setA1c] = useState("");
  const v = num(a1c);
  const show = Number.isFinite(v) && v > 0;
  const res = show ? a1cToEag(v) : null;
  const summary = show ? `${tt("summaryTitle")}\n\nA1C: ${v}%\n${tt("eag")}: ${res.mgdl} mg/dL (${res.mmol} mmol/L)` : "";
  return (
    <div className="space-y-5">
      <Field label={tt("a1c")} hint={tt("a1cHint")}>
        <NumberInput value={a1c} onChange={setA1c} placeholder="7.0" testId="in-a1c" />
      </Field>
      <Result show={show} summary={summary} question={show ? `My A1C is ${v}%. What does that mean for my blood sugar?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("eag")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-eag">{res?.mgdl} mg/dL <span className="text-xl font-bold opacity-70">· {res?.mmol} mmol/L</span></p>
        <p className="text-sm mt-2 opacity-80">{tt("eagNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 2. Glucose -> A1C ----------
function GlucoseToA1c() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("mgdl");
  const [g, setG] = useState("");
  const raw = num(g);
  const mgdl = unit === "mgdl" ? raw : conv.glucoseToMgdl(raw);
  const show = Number.isFinite(mgdl) && mgdl > 0;
  const a1c = show ? glucoseToA1c(mgdl) : null;
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("gluAvg")}: ${g} ${unit === "mgdl" ? "mg/dL" : "mmol/L"}\n${tt("a1cOut")}: ${a1c}%` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={mmolBtn(tt)} value={unit} onChange={setUnit} /></div>
      <Field label={`${tt("gluAvg")} (${unit === "mgdl" ? "mg/dL" : "mmol/L"})`}>
        <NumberInput value={g} onChange={setG} placeholder={unit === "mgdl" ? "154" : "8.5"} testId="in-glucose" />
      </Field>
      <Result show={show} summary={summary} question={show ? `My average blood sugar is ${g} ${unit === "mgdl" ? "mg/dL" : "mmol/L"}. What A1C is that and is it healthy?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("a1cOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-a1c">{a1c}%</p>
        <p className="text-sm mt-2 opacity-80">{tt("a1cOutNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 3. BMI ----------
function Bmi() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("us");
  const [w, setW] = useState("");
  const [h, setH] = useState("");
  const [hIn, setHIn] = useState("");
  const weightKg = unit === "us" ? conv.lbToKg(num(w)) : num(w);
  const heightCm = unit === "us" ? conv.inToCm(num(h) * 12 + (num(hIn) || 0)) : num(h);
  const show = ok(weightKg, heightCm);
  const res = show ? bmi(weightKg, heightCm) : null;
  const cat = res ? tt(`bmi_${res.key}`) : "";
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("bmiOut")}: ${res.value} (${cat})` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={[{ value: "us", label: tt("unitLbft") }, { value: "metric", label: tt("unitMetric") }]} value={unit} onChange={setUnit} /></div>
      <Field label={`${tt("weight")} (${unit === "us" ? tt("unitLb") : tt("unitKg")})`}>
        <NumberInput value={w} onChange={setW} placeholder={unit === "us" ? "170" : "77"} testId="in-weight" />
      </Field>
      {unit === "us" ? (
        <div className="grid grid-cols-2 gap-4">
          <Field label={tt("heightFt")}><NumberInput value={h} onChange={setH} placeholder="5" testId="in-height-ft" /></Field>
          <Field label={tt("heightIn")}><NumberInput value={hIn} onChange={setHIn} placeholder="9" testId="in-height-in" /></Field>
        </div>
      ) : (
        <Field label={tt("heightCm")}><NumberInput value={h} onChange={setH} placeholder="175" testId="in-height-cm" /></Field>
      )}
      <Result show={show} tone={res?.tone} summary={summary} question={show ? `My BMI is ${res.value} (${cat}). What does this mean for my health?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("bmiOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-bmi">{res?.value} <span className="text-xl font-bold">· {cat}</span></p>
        <p className="text-sm mt-2 opacity-80">{tt("bmiNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 4. Blood pressure ----------
function BloodPressure() {
  const { tt } = useToolsT();
  const [sys, setSys] = useState("");
  const [dia, setDia] = useState("");
  const s = num(sys), d = num(dia);
  const show = ok(s, d);
  const res = show ? bpCategory(s, d) : null;
  const cat = res ? tt(`bp_${res.key}`) : "";
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("sys")}/${tt("dia")}: ${s}/${d} mmHg\n${tt("bpOut")}: ${cat}\n${res ? tt(`bpx_${res.key}`) : ""}` : "";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label={tt("sys")}><NumberInput value={sys} onChange={setSys} placeholder="128" testId="in-sys" /></Field>
        <Field label={tt("dia")}><NumberInput value={dia} onChange={setDia} placeholder="82" testId="in-dia" /></Field>
      </div>
      <Result show={show} tone={res?.tone} summary={summary} question={show ? `My blood pressure is ${s}/${d}. What does "${cat}" mean and what should I do?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("bpOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-bp">{cat}</p>
        <p className="text-sm mt-2 opacity-90">{res ? tt(`bpx_${res.key}`) : ""}</p>
      </Result>
    </div>
  );
}

// ---------- 5. eGFR ----------
function Egfr() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("mgdl");
  const [scr, setScr] = useState("");
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("female");
  const scrMgdl = unit === "mgdl" ? num(scr) : conv.creatToMgdl(num(scr));
  const a = num(age);
  const show = ok(scrMgdl, a);
  const res = show ? egfrCKDEPI(scrMgdl, a, sex) : null;
  const stage = res ? tt(`egfr_${res.stageKey.key}`) : "";
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("egfrOut")}: ${res.value} ${tt("egfrUnit")}\n${stage}` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={[{ value: "mgdl", label: "mg/dL" }, { value: "umol", label: "µmol/L" }]} value={unit} onChange={setUnit} /></div>
      <Field label={`${tt("scr")} (${unit === "mgdl" ? "mg/dL" : "µmol/L"})`}>
        <NumberInput value={scr} onChange={setScr} placeholder={unit === "mgdl" ? "1.0" : "88"} testId="in-scr" />
      </Field>
      <div className="grid grid-cols-2 gap-4 items-end">
        <Field label={tt("age")}><NumberInput value={age} onChange={setAge} placeholder="55" testId="in-age" /></Field>
        <Field label={tt("sex")}><SexToggle value={sex} onChange={setSex} tt={tt} /></Field>
      </div>
      <Result show={show} tone={res?.stageKey.tone} summary={summary} question={show ? `My estimated GFR is ${res.value} mL/min/1.73m2. What does this mean for my kidneys?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("egfrOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-egfr">{res?.value} <span className="text-lg font-bold">{tt("egfrUnit")}</span></p>
        <p className="text-sm mt-2 font-semibold">{stage}</p>
        <p className="text-sm mt-1 opacity-80">{tt("egfrNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 6. Creatinine clearance ----------
function CreatinineClearance() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("us");
  const [creatUnit, setCreatUnit] = useState("mgdl");
  const [age, setAge] = useState("");
  const [w, setW] = useState("");
  const [scr, setScr] = useState("");
  const [sex, setSex] = useState("female");
  const weightKg = unit === "us" ? conv.lbToKg(num(w)) : num(w);
  const scrMgdl = creatUnit === "mgdl" ? num(scr) : conv.creatToMgdl(num(scr));
  const a = num(age);
  const show = ok(weightKg, scrMgdl, a);
  const res = show ? cockcroftGault(a, weightKg, scrMgdl, sex) : null;
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("crclOut")}: ${res.value} ${tt("crclUnit")}` : "";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 items-end">
        <Field label={tt("age")}><NumberInput value={age} onChange={setAge} placeholder="60" testId="in-age" /></Field>
        <Field label={tt("sex")}><SexToggle value={sex} onChange={setSex} tt={tt} /></Field>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-semibold text-slate-700">{tt("weight")}</span>
          <UnitToggle options={[{ value: "us", label: tt("unitLb") }, { value: "metric", label: tt("unitKg") }]} value={unit} onChange={setUnit} />
        </div>
        <NumberInput value={w} onChange={setW} placeholder={unit === "us" ? "165" : "75"} testId="in-weight" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-semibold text-slate-700">{tt("scr")}</span>
          <UnitToggle options={[{ value: "mgdl", label: "mg/dL" }, { value: "umol", label: "µmol/L" }]} value={creatUnit} onChange={setCreatUnit} />
        </div>
        <NumberInput value={scr} onChange={setScr} placeholder={creatUnit === "mgdl" ? "1.0" : "88"} testId="in-scr" />
      </div>
      <Result show={show} summary={summary} question={show ? `My creatinine clearance is about ${res.value} mL/min. What does that mean?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("crclOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-crcl">{res?.value} <span className="text-lg font-bold">{tt("crclUnit")}</span></p>
        <p className="text-sm mt-2 opacity-80">{tt("crclNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 7. Cholesterol risk ----------
function CholesterolRisk() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("mgdl");
  const [tc, setTc] = useState("");
  const [hdl, setHdl] = useState("");
  const [f, setF] = useState({ smoker: false, diabetes: false, highBP: false, familyHistory: false });
  const tcMgdl = unit === "mgdl" ? num(tc) : conv.cholToMgdl(num(tc));
  const hdlMgdl = unit === "mgdl" ? num(hdl) : conv.cholToMgdl(num(hdl));
  const show = ok(tcMgdl, hdlMgdl);
  const res = show ? cholesterolRisk(tcMgdl, hdlMgdl, f) : null;
  const level = res ? tt(`chol_${res.levelKey}`) : "";
  const toggle = (k) => setF((s) => ({ ...s, [k]: !s[k] }));
  const flagsText = res && res.factorCount > 0
    ? tt("cholFlags", { n: res.factorCount, list: res.flags.map((k) => tt(`factor_${k}`)).join(", ") })
    : tt("cholNoFlags");
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("cholOut")}: ${res.ratio} (${level})\n${flagsText}` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={mmolBtn(tt)} value={unit} onChange={setUnit} /></div>
      <div className="grid grid-cols-2 gap-4">
        <Field label={`${tt("tc")} (${unit === "mgdl" ? "mg/dL" : "mmol/L"})`}><NumberInput value={tc} onChange={setTc} placeholder={unit === "mgdl" ? "200" : "5.2"} testId="in-tc" /></Field>
        <Field label={`${tt("hdl")} (${unit === "mgdl" ? "mg/dL" : "mmol/L"})`}><NumberInput value={hdl} onChange={setHdl} placeholder={unit === "mgdl" ? "50" : "1.3"} testId="in-hdl" /></Field>
      </div>
      <div>
        <span className="block text-sm font-semibold text-slate-700 mb-2">{tt("cholFactors")}</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {["smoker", "diabetes", "highBP", "familyHistory"].map((k) => (
            <button key={k} type="button" onClick={() => toggle(k)} data-testid={`factor-${k}`}
              className={`text-left px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-colors ${f[k] ? "border-violet-400 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {f[k] ? "✓ " : ""}{tt(`factor_${k}`)}
            </button>
          ))}
        </div>
      </div>
      <Result show={show} tone={res?.tone} summary={summary} question={show ? `My total cholesterol to HDL ratio is ${res.ratio}. What does that mean for my heart health?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("cholOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-chol-ratio">{res?.ratio} <span className="text-xl font-bold">· {level}</span></p>
        <p className="text-sm mt-2 opacity-90">{flagsText}</p>
        <p className="text-sm mt-2 opacity-80">{tt("cholNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 8. LDL ----------
function Ldl() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("mgdl");
  const [tc, setTc] = useState("");
  const [hdl, setHdl] = useState("");
  const [tg, setTg] = useState("");
  const tcMgdl = unit === "mgdl" ? num(tc) : conv.cholToMgdl(num(tc));
  const hdlMgdl = unit === "mgdl" ? num(hdl) : conv.cholToMgdl(num(hdl));
  const tgMgdl = unit === "mgdl" ? num(tg) : conv.tgToMgdl(num(tg));
  const show = ok(tcMgdl, hdlMgdl, tgMgdl);
  const res = show ? ldlFriedewald(tcMgdl, hdlMgdl, tgMgdl) : null;
  const ldlOut = res?.valid ? (unit === "mgdl" ? `${res.value} mg/dL` : `${Math.round(conv.cholToMmol(res.value) * 10) / 10} mmol/L`) : null;
  const summary = res?.valid ? `${tt("summaryTitle")}\n\n${tt("ldlOut")}: ${ldlOut}` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={mmolBtn(tt)} value={unit} onChange={setUnit} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label={tt("tc")}><NumberInput value={tc} onChange={setTc} placeholder={unit === "mgdl" ? "200" : "5.2"} testId="in-tc" /></Field>
        <Field label={tt("hdl")}><NumberInput value={hdl} onChange={setHdl} placeholder={unit === "mgdl" ? "50" : "1.3"} testId="in-hdl" /></Field>
        <Field label={tt("tg")}><NumberInput value={tg} onChange={setTg} placeholder={unit === "mgdl" ? "150" : "1.7"} testId="in-tg" /></Field>
      </div>
      <Result show={show} tone={res?.valid ? "neutral" : "amber"} summary={summary} question={res?.valid ? `My LDL cholesterol is ${ldlOut}. Is that healthy and what should I do?` : ""} tt={tt}>
        {res?.valid ? (
          <>
            <p className="text-sm font-semibold opacity-70">{tt("ldlOut")}</p>
            <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-ldl">{ldlOut}</p>
            <p className="text-sm mt-2 opacity-80">{tt("ldlNote")}</p>
          </>
        ) : (
          <>
            <p className="text-lg font-heading font-bold" data-testid="out-ldl">{tt("ldlInvalid")}</p>
            <p className="text-sm mt-1 opacity-90">{tt("ldlInvalidNote")}</p>
          </>
        )}
      </Result>
    </div>
  );
}

// ---------- 9. Non-HDL ----------
function NonHdl() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("mgdl");
  const [tc, setTc] = useState("");
  const [hdl, setHdl] = useState("");
  const tcMgdl = unit === "mgdl" ? num(tc) : conv.cholToMgdl(num(tc));
  const hdlMgdl = unit === "mgdl" ? num(hdl) : conv.cholToMgdl(num(hdl));
  const show = ok(tcMgdl, hdlMgdl);
  const res = show ? nonHdl(tcMgdl, hdlMgdl) : null;
  const out = show ? (unit === "mgdl" ? `${res.value} mg/dL` : `${Math.round(conv.cholToMmol(res.value) * 10) / 10} mmol/L`) : null;
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("nonhdlOut")}: ${out}` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={mmolBtn(tt)} value={unit} onChange={setUnit} /></div>
      <div className="grid grid-cols-2 gap-4">
        <Field label={tt("tc")}><NumberInput value={tc} onChange={setTc} placeholder={unit === "mgdl" ? "200" : "5.2"} testId="in-tc" /></Field>
        <Field label={tt("hdl")}><NumberInput value={hdl} onChange={setHdl} placeholder={unit === "mgdl" ? "50" : "1.3"} testId="in-hdl" /></Field>
      </div>
      <Result show={show} summary={summary} question={show ? `My non-HDL cholesterol is ${out}. What does that mean?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("nonhdlOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-nonhdl">{out}</p>
        <p className="text-sm mt-2 opacity-80">{tt("nonhdlNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 10. Target heart rate ----------
function TargetHeartRate() {
  const { tt } = useToolsT();
  const [age, setAge] = useState("");
  const [rest, setRest] = useState("");
  const a = num(age);
  const r = num(rest);
  const show = Number.isFinite(a) && a > 0 && a < 120;
  const res = show ? targetHeartRate(a, r) : null;
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("hrMax")}: ${res.maxHR} ${tt("hrUnit")}\n${tt("hrModerate")}: ${res.moderate[0]}–${res.moderate[1]} ${tt("hrUnit")}\n${tt("hrVigorous")}: ${res.vigorous[0]}–${res.vigorous[1]} ${tt("hrUnit")}` : "";
  return (
    <div className="space-y-5">
      <Field label={tt("age")}><NumberInput value={age} onChange={setAge} placeholder="40" testId="in-age" /></Field>
      <Field label={tt("hrResting")} hint={tt("hrRestingHint")}><NumberInput value={rest} onChange={setRest} placeholder="65" testId="in-resting" /></Field>
      <Result show={show} tone="neutral" summary={summary} question={show ? `My age is ${a}. What heart rate should I aim for during exercise?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("hrMax")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-hr-max">{res?.maxHR} <span className="text-lg font-bold">{tt("hrUnit")}</span></p>
        <div className="mt-3 space-y-1.5 text-base">
          <p><span className="font-semibold">{tt("hrModerate")}:</span> {res?.moderate[0]}–{res?.moderate[1]} {tt("hrUnit")}</p>
          <p><span className="font-semibold">{tt("hrVigorous")}:</span> {res?.vigorous[0]}–{res?.vigorous[1]} {tt("hrUnit")}</p>
        </div>
        <p className="text-sm mt-2 opacity-80">{tt("hrNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 11. Due date ----------
function DueDate() {
  const { tt } = useToolsT();
  const [lmp, setLmp] = useState("");
  const res = lmp ? dueDate(lmp) : null;
  const show = !!res;
  const gaVal = res && res.gaWeeks !== null ? tt("ddGaVal", { w: res.gaWeeks, d: res.gaDays }) : null;
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("ddEdd")}: ${res.edd}${gaVal ? `\n${tt("ddGa")}: ${gaVal}` : ""}` : "";
  return (
    <div className="space-y-5">
      <Field label={tt("ddLmp")}>
        <input type="date" value={lmp} onChange={(e) => setLmp(e.target.value)} data-testid="in-lmp" className={inputCls} />
      </Field>
      <Result show={show} tone="neutral" summary={summary} question={show ? `My due date is ${res?.edd}. What should I expect at this stage of pregnancy?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("ddEdd")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-edd">{res?.edd}</p>
        {gaVal && <p className="text-base mt-2"><span className="font-semibold">{tt("ddGa")}:</span> {gaVal}</p>}
        {res?.daysToGo > 0 && <p className="text-sm mt-1 opacity-80">{tt("ddTogo", { n: res.daysToGo })}</p>}
        {res && res.daysToGo === 0 && res.gaWeeks !== null && <p className="text-sm mt-1 opacity-80">{tt("ddBorn")}</p>}
        <p className="text-sm mt-2 opacity-80">{tt("ddNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 12. Weight-based dose ----------
function MedicationDose() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("us");
  const [w, setW] = useState("");
  const [mgkg, setMgkg] = useState("");
  const [max, setMax] = useState("");
  const weightKg = unit === "us" ? conv.lbToKg(num(w)) : num(w);
  const mk = num(mgkg);
  const mx = num(max);
  const show = ok(weightKg, mk);
  const res = show ? weightBasedDose(weightKg, mk, mx) : null;
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("doseOut")}: ${res.dose} ${tt("doseUnit")}${res.capped ? ` (${tt("doseCapped")})` : ""}` : "";
  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-semibold text-slate-700">{tt("doseWeight")}</span>
          <UnitToggle options={[{ value: "us", label: tt("unitLb") }, { value: "metric", label: tt("unitKg") }]} value={unit} onChange={setUnit} />
        </div>
        <NumberInput value={w} onChange={setW} placeholder={unit === "us" ? "44" : "20"} testId="in-weight" />
      </div>
      <Field label={tt("doseMgkg")}><NumberInput value={mgkg} onChange={setMgkg} placeholder="15" testId="in-mgkg" /></Field>
      <Field label={tt("doseMax")} hint={tt("doseMaxHint")}><NumberInput value={max} onChange={setMax} placeholder="1000" testId="in-max" /></Field>
      <Result show={show} tone="neutral" summary={summary} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("doseOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-dose">{res?.dose} <span className="text-lg font-bold">{tt("doseUnit")}</span></p>
        {res?.capped && <p className="text-sm mt-1 font-semibold text-amber-700">{tt("doseCapped")}</p>}
        <p className="text-sm mt-2 opacity-80">{tt("doseNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 13. Ideal body weight ----------
function IdealBodyWeight() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("us");
  const [ft, setFt] = useState("");
  const [inch, setInch] = useState("");
  const [cm, setCm] = useState("");
  const [sex, setSex] = useState("female");
  const heightCm = unit === "us" ? conv.inToCm(num(ft) * 12 + (num(inch) || 0)) : num(cm);
  const show = Number.isFinite(heightCm) && heightCm > 100;
  const res = show ? idealBodyWeight(heightCm, sex) : null;
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("ibwOut")}: ${res.kg} kg (${res.lb} lb)` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={[{ value: "us", label: tt("unitLbft") }, { value: "metric", label: tt("unitMetric") }]} value={unit} onChange={setUnit} /></div>
      {unit === "us" ? (
        <div className="grid grid-cols-2 gap-4">
          <Field label={tt("heightFt")}><NumberInput value={ft} onChange={setFt} placeholder="5" testId="in-height-ft" /></Field>
          <Field label={tt("heightIn")}><NumberInput value={inch} onChange={setInch} placeholder="9" testId="in-height-in" /></Field>
        </div>
      ) : (
        <Field label={tt("heightCm")}><NumberInput value={cm} onChange={setCm} placeholder="175" testId="in-height-cm" /></Field>
      )}
      <Field label={tt("sex")}><SexToggle value={sex} onChange={setSex} tt={tt} /></Field>
      <Result show={show} tone="neutral" summary={summary} question={show ? `My ideal body weight is about ${res.kg} kg. Is my current weight healthy?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("ibwOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-ibw">{res?.kg} kg <span className="text-xl font-bold opacity-70">· {res?.lb} lb</span></p>
        <p className="text-sm mt-2 opacity-80">{tt("ibwNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 14. Waist-to-height ratio ----------
function WaistHeight() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("cm");
  const [waist, setWaist] = useState("");
  const [height, setHeight] = useState("");
  const waistCm = unit === "cm" ? num(waist) : conv.inToCm(num(waist));
  const heightCm = unit === "cm" ? num(height) : conv.inToCm(num(height));
  const show = ok(waistCm, heightCm);
  const res = show ? waistToHeight(waistCm, heightCm) : null;
  const cat = res ? tt(`wht_${res.key}`) : "";
  const summary = show ? `${tt("summaryTitle")}\n\n${tt("wthOut")}: ${res.ratio} (${cat})` : "";
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><UnitToggle options={[{ value: "cm", label: "cm" }, { value: "in", label: "in" }]} value={unit} onChange={setUnit} /></div>
      <div className="grid grid-cols-2 gap-4">
        <Field label={`${tt("waist")} (${unit})`}><NumberInput value={waist} onChange={setWaist} placeholder={unit === "cm" ? "84" : "33"} testId="in-waist" /></Field>
        <Field label={`${unit === "cm" ? tt("heightCm") : tt("heightFt")} (${unit})`}><NumberInput value={height} onChange={setHeight} placeholder={unit === "cm" ? "170" : "67"} testId="in-height" /></Field>
      </div>
      <Result show={show} tone={res?.tone} summary={summary} question={show ? `My waist-to-height ratio is ${res.ratio} (${cat}). What does that mean for my health?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("wthOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-wht">{res?.ratio} <span className="text-xl font-bold">· {cat}</span></p>
        <p className="text-sm mt-2 opacity-80">{tt("wthNote")}</p>
      </Result>
    </div>
  );
}

// ---------- 15. Pediatric fever dosing ----------
function PediatricFever() {
  const { tt } = useToolsT();
  const [unit, setUnit] = useState("us");
  const [drug, setDrug] = useState("acetaminophen");
  const [concId, setConcId] = useState(PED_CONC.acetaminophen[0].id);
  const [w, setW] = useState("");
  const weightKg = unit === "us" ? conv.lbToKg(num(w)) : num(w);
  const show = ok(weightKg);
  const res = show ? pediatricFever(weightKg, drug) : null;
  const list = PED_CONC[drug];
  const conc = list.find((c) => c.id === concId) || list[0];
  const ml = res ? doseToMl(res.perDose, conc) : null;
  const summary = show
    ? `${tt("summaryTitle")}\n\n${tt(drug === "ibuprofen" ? "pfIbu" : "pfAcet")}: ${res.perDose} mg (≈ ${ml} ${tt("pfMlUnit")} · ${conc.mg} mg/${conc.ml} mL)\n${tt(res.interval)}`
    : "";
  return (
    <div className="space-y-5">
      <Field label={tt("pfDrug")}>
        <div className="flex rounded-xl border-2 border-slate-200 overflow-hidden">
          {[["acetaminophen", tt("pfAcet")], ["ibuprofen", tt("pfIbu")]].map(([d, label]) => (
            <button key={d} type="button" onClick={() => setDrug(d)} data-testid={`drug-${d}`}
              className={`flex-1 py-2.5 text-base font-semibold transition-colors ${drug === d ? "bg-rose-600 text-white" : "text-slate-700 hover:bg-rose-50"}`}>{label}</button>
          ))}
        </div>
      </Field>
      <Field label={tt("pfConc")}>
        <div className="flex flex-wrap gap-2">
          {list.map((c) => (
            <button key={c.id} type="button" onClick={() => setConcId(c.id)} data-testid={`conc-${c.id}`}
              className={`px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-colors ${conc.id === c.id ? "border-rose-400 bg-rose-50 text-rose-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {c.mg} mg/{c.ml} mL <span className="font-normal opacity-70">· {tt(c.typeKey)}</span>
            </button>
          ))}
        </div>
      </Field>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-semibold text-slate-700">{tt("pfWeight")}</span>
          <UnitToggle options={[{ value: "us", label: tt("unitLb") }, { value: "metric", label: tt("unitKg") }]} value={unit} onChange={setUnit} />
        </div>
        <NumberInput value={w} onChange={setW} placeholder={unit === "us" ? "33" : "15"} testId="in-weight" />
      </div>
      <Result show={show} tone="neutral" summary={summary} question={show ? `How much ${drug} can I give my child who weighs ${w} ${unit === "us" ? "lb" : "kg"}?` : ""} tt={tt}>
        <p className="text-sm font-semibold opacity-70">{tt("pfOut")}</p>
        <p className="text-3xl font-heading font-extrabold mt-1" data-testid="out-pf">{res?.perDose} <span className="text-lg font-bold">mg</span></p>
        {ml != null && (
          <p className="text-2xl font-heading font-extrabold mt-1 text-rose-700" data-testid="out-pf-ml">
            ≈ {ml} {tt("pfMlUnit")} <span className="text-base font-bold opacity-70">· {conc.mg} mg/{conc.ml} mL</span>
          </p>
        )}
        <p className="text-sm mt-2 font-semibold">{res ? tt(res.interval) : ""}</p>
        {res?.capped && <p className="text-sm mt-1 text-amber-700 font-semibold">{tt("pfCapped")}</p>}
        <p className="text-sm mt-2 opacity-90">⚠️ {res ? tt(res.warnKey) : ""}</p>
        <p className="text-sm mt-1 opacity-80">{tt("pfMlNote")}</p>
        <p className="text-sm mt-2 opacity-80">{tt("pfNote")}</p>
      </Result>
    </div>
  );
}

export const CALCULATORS = {
  "a1c-to-glucose": A1cToGlucose,
  "glucose-to-a1c": GlucoseToA1c,
  "bmi": Bmi,
  "blood-pressure": BloodPressure,
  "egfr": Egfr,
  "creatinine-clearance": CreatinineClearance,
  "cholesterol-risk": CholesterolRisk,
  "ldl": Ldl,
  "non-hdl": NonHdl,
  "target-heart-rate": TargetHeartRate,
  "due-date": DueDate,
  "medication-dose": MedicationDose,
  "ideal-body-weight": IdealBodyWeight,
  "waist-height-ratio": WaistHeight,
  "pediatric-fever": PediatricFever,
};

// Compact calculator card shown inline within an AI answer when a tool is relevant.
export function InlineToolCard({ slug }) {
  const { tt, reg } = useToolsT();
  const tool = toolBySlug(slug);
  const Calc = CALCULATORS[slug];
  if (!tool || !Calc) return null;
  const c = COLOR[tool.color];
  const Icon = tool.icon;
  const meta = reg(slug);
  return (
    <div className={`rounded-2xl border-2 ${c.ring} bg-white p-5 sm:p-6`} data-testid={`inline-tool-${slug}`} data-no-print>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className={`inline-grid place-items-center w-11 h-11 rounded-xl bg-gradient-to-br ${c.grad} text-white shadow-sm shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{tt("inlineTitle")}</p>
            <h3 className="font-heading font-bold text-lg text-slate-900 leading-tight">{meta.t}</h3>
          </div>
        </div>
        <Link
          to={`/tools/${slug}`}
          data-testid="inline-tool-open"
          className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-teal-800 shrink-0"
        >
          {tt("inlineOpen")} <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <Calc />
    </div>
  );
}
